import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AccountLegalNotice } from "../components/AccountLegalNotice";
import { HCaptchaField } from "../components/HCaptchaField";
import { OAuthLoginButtons } from "../components/OAuthLoginButtons";
import { useCustomer } from "../customer/CustomerContext";
import {
  emailMessage,
  isValidEmail,
  requiredMessage,
} from "../utils/formValidation";

export function LoginPage() {
  const { customer, login } = useCustomer();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const oauthError = (searchParams.get("error") || "").trim();
  const resetOk = Boolean((location.state as { resetOk?: boolean } | null)?.resetOk);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(oauthError || null);
  const [loading, setLoading] = useState(false);

  if (customer) return <Navigate to="/account" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = requiredMessage("Email");
    else if (!isValidEmail(email)) errs.email = emailMessage();
    if (!password) errs.password = requiredMessage("Password");
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError("Please fill in the required fields.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await login(email.trim(), password, captchaToken);
      navigate("/account");
    } catch (err) {
      const fe = (err as { field_errors?: Record<string, string> }).field_errors;
      if (fe) setFieldErrors(fe);
      setError(err instanceof Error ? err.message : "Login failed.");
      setCaptchaToken("");
      setCaptchaReset((n) => n + 1);
    } finally {
      setLoading(false);
    }
  }

  return (
    <article className="page account-page">
      <p className="eyebrow">Customer account</p>
      <h1>Log in</h1>
      <p className="page-summary">
        Manage purchases, favorites, language, and currency for your TNK services.
      </p>
      {resetOk ? (
        <p className="account-reset-done" role="status">
          Password updated. You can log in with your new password.
        </p>
      ) : null}
      <form className="deposit-form account-form" onSubmit={onSubmit} noValidate>
        <div className="buy-server-row">
          <label htmlFor="login-email">
            Email <span className="req-star">*</span>
          </label>
          <div className="buy-server-control">
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              aria-invalid={Boolean(fieldErrors.email)}
              onChange={(e) => setEmail(e.target.value)}
            />
            {fieldErrors.email ? <p className="field-error">{fieldErrors.email}</p> : null}
          </div>
        </div>
        <div className="buy-server-row">
          <label htmlFor="login-password">
            Password <span className="req-star">*</span>
          </label>
          <div className="buy-server-control">
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              aria-invalid={Boolean(fieldErrors.password)}
              onChange={(e) => setPassword(e.target.value)}
            />
            {fieldErrors.password ? <p className="field-error">{fieldErrors.password}</p> : null}
            <p className="forgot-password-link">
              <Link to="/account/forgot-password">Forgot password?</Link>
            </p>
          </div>
        </div>
        <HCaptchaField purpose="login" onToken={setCaptchaToken} resetKey={captchaReset} />
        {fieldErrors.captcha ? <p className="field-error">{fieldErrors.captcha}</p> : null}
        {error ? (
          <p className="model-run-error" role="alert">
            {error}
          </p>
        ) : null}
        <AccountLegalNotice mode="login" />
        <div className="buy-server-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Signing in…" : "Log in"}
          </button>
        </div>
      </form>
      <OAuthLoginButtons disabled={loading} />
      <p className="account-switch">
        New here? <Link to="/account/register">Create an account</Link>
      </p>
    </article>
  );
}
